import { useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import workspaceService from "api/workspaceService";
import { IBillingInfo } from "dto/IBillingInfo";
import { IPaymentMethodView } from "dto/IPaymentMethod";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import ConfirmModal from "components/ConfirmModal";
import EditIcon from "assets/icons/edite.svg?react";
import CardIcon from "assets/icons/credit_card.svg?react";
import { Checkbox } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCreditCard,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { ENotificationType, notification } from "utils/notification";
import EditBuyerPanel from "./EditBuyerPanel";
import EditCardPanel from "./EditCardPanel";

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div className={styles.row}>
    <div className={styles.rowLabel}>{label}</div>
    <div className={styles.rowValue}>{value || "—"}</div>
  </div>
);

type View = "main" | "buyer" | "card";

const PaymentMethods = () => {
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  const { data: billingData, refetch: refetchBilling } =
    workspaceService.useGetBillingInfo(workspaceId);
  const { data: cardData, refetch: refetchCard } =
    workspaceService.useGetPaymentMethod(workspaceId);

  const info: IBillingInfo = useMemo(() => billingData?.data, [billingData]);
  const card: IPaymentMethodView | null = useMemo(
    () => cardData?.data ?? null,
    [cardData],
  );

  const [view, setView] = useState<View>("main");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const doRemoveCard = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      await workspaceService.removePaymentMethod(workspaceId);
      notification({
        type: ENotificationType.SUCCESS,
        title: "Karta płatnicza",
        text: "Karta została usunięta.",
      });
      refetchCard();
    } catch {
      notification({
        type: ENotificationType.ERROR,
        title: "Karta płatnicza",
        text: "Nie udało się usunąć karty.",
      });
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  if (view === "buyer") {
    return (
      <EditBuyerPanel
        workspaceId={workspaceId}
        info={info}
        onBack={() => setView("main")}
        onSaved={() => {
          setView("main");
          refetchBilling();
        }}
      />
    );
  }

  if (view === "card") {
    return (
      <EditCardPanel
        workspaceId={workspaceId}
        onBack={() => setView("main")}
        onSaved={() => {
          setView("main");
          refetchCard();
        }}
      />
    );
  }

  const address = [
    info?.Street,
    [info?.ZipCode, info?.City].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const hasCard = !!card?.Last4;
  const expiry =
    card?.ExpMonth && card?.ExpYear
      ? `${String(card.ExpMonth).padStart(2, "0")}/${card.ExpYear}`
      : "";

  return (
    <div className={styles.tab}>
      {/* --- Karta kredytowa --- */}
      <div className={styles.sectionTitle}>Karta kredytowa</div>

      {hasCard ? (
        <div className={styles.cardBox}>
          <div className={styles.cardLeft}>
            <FontAwesomeIcon icon={faCreditCard} className={styles.cardIcon} />
            <div>
              <div className={styles.cardLabel}>{card?.Label}</div>
              {expiry && (
                <div className={styles.cardExpiry}>Data ważności {expiry}</div>
              )}
            </div>
          </div>
          <div className={styles.cardCheck}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
        </div>
      ) : (
        <div className={styles.cardEmpty}>Brak zapisanej karty.</div>
      )}

      <div className={styles.cardActions}>
        <Button
          onClick={() => setView("card")}
          element={
            <span className={styles.btnContent}>
              <FontAwesomeIcon icon={hasCard ? faCreditCard : faPlus} />{" "}
              {hasCard ? "Zmień kartę" : "Dodaj kartę płatniczą"}
            </span>
          }
        />
        {hasCard && (
          <HollowButton
            text="Usuń kartę"
            onClick={() => setConfirmRemove(true)}
            element={
              <span className={styles.btnContent}>
                <FontAwesomeIcon icon={faTrash} /> Usuń kartę
              </span>
            }
          />
        )}
      </div>

      <div className={styles.divLine}></div>

      {/* --- Informacje rozliczeniowe --- */}
      <div className={styles.sectionTitle}>Informacje rozliczeniowe</div>
      <div className={styles.hint}>
        Dane nabywcy wykorzystywane do wystawiania faktur za subskrypcję.
      </div>

      <div className={styles.rows}>
        <InfoRow label="Nazwa" value={info?.CompanyName} />
        <InfoRow label="NIP" value={info?.NIP} />
        <InfoRow label="Adres" value={address} />
        <InfoRow label="Imię i nazwisko" value={info?.ContactName} />
        <InfoRow label="Email" value={info?.Email} />
        <InfoRow label="Telefon" value={info?.Phone} />
      </div>

      <div className={styles.issue}>
        <Checkbox
          size="small"
          color="primary"
          checked={!!info?.IssueInvoice}
          disabled
          sx={{ padding: 0 }}
        />
        <span>Wystaw fakturę</span>
      </div>

      <div className={styles.actions}>
        <Button
          onClick={() => setView("buyer")}
          element={
            <span className={styles.btnContent}>
              <EditIcon className={styles.btnIcon} /> Zmień dane
            </span>
          }
        />
      </div>

      {confirmRemove && (
        <ConfirmModal
          Icon={CardIcon}
          title="Usunięcie karty"
          text="Czy na pewno chcesz usunąć zapisaną kartę? Bez karty nie pobierzemy płatności za subskrypcję — aby aktywować/utrzymać pakiet, dodaj nową kartę."
          cancelLabel="Anuluj"
          acceptLabel="Usuń kartę"
          showDontShowNextTime={false}
          onCancel={() => setConfirmRemove(false)}
          onAccept={doRemoveCard}
        />
      )}
    </div>
  );
};

export default PaymentMethods;
